import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeftRight, Banknote, CheckCircle2, FileUp, Landmark, Plus, RefreshCw, Wand2 } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/currency';
import { Modal, Btn, INPUT, LABEL, fmtDate, localToday, errMsg } from '../../components/crm/billing/ui';
import { resetBankAccountCache } from '../../components/crm/billing/BankAccountFields';
import Tabs from '../../components/ui/Tabs';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';

const TABS = [
  { key: 'accounts', label: 'Accounts', icon: Landmark },
  { key: 'reconcile', label: 'Reconcile', icon: CheckCircle2 },
  { key: 'transactions', label: 'Transfers & settlements', icon: ArrowLeftRight },
  { key: 'cheques', label: 'Cheques', icon: Banknote },
];
const KIND_LABEL = { bank: 'Bank', cash: 'Cash', gateway: 'Payment gateway' };
const SOURCE_LABEL = {
  receipt: 'Receipt', payment: 'Invoice payment', refund: 'Refund', bill_payment: 'Vendor payment', advance_refund: 'Advance refund',
  bank_transaction: 'Bank transaction', bank_transaction_void: 'Voided bank transaction', receipt_void: 'Receipt voided',
  bill_payment_void: 'Vendor payment voided', manual: 'Journal entry',
};
const Pill = ({ tone, children }) => (
  <span className={cn('inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide',
    tone === 'good' ? 'bg-[#E6F4EA] text-[#2E844A]' : tone === 'bad' ? 'bg-[#FEF1EE] text-[#BA0517]' : tone === 'warn' ? 'bg-[#FEF8E8] text-[#7A5300]' : 'bg-[#F3F2F2] text-[#706E6B]')}>{children}</span>
);
const Th = ({ children, right }) => <th className={cn('px-3 py-2 font-semibold', right ? 'text-right' : 'text-left')}>{children}</th>;
const Td = ({ children, right, className }) => <td className={cn('px-3 py-2', right && 'text-right tabular-nums', className)}>{children}</td>;
function Table({ head, children, empty }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
      <table className="w-full text-[12px] sm:min-w-[40rem]">
        <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr>{head}</tr></thead>
        <tbody>{children}</tbody>
      </table>
      {empty && <p className="text-[13px] text-[#706E6B] text-center py-8">{empty}</p>}
    </div>
  );
}

// ── Accounts ─────────────────────────────────────────────────────────────

function AccountModal({ account, onClose, onSaved }) {
  const [f, setF] = useState(account ? { ...account } : { kind: 'bank', name: '', bankName: '', accountNumberLast4: '', ifsc: '', gatewayProvider: '', currency: '' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const body = { name: f.name, kind: f.kind, bankName: f.bankName, accountNumberLast4: f.accountNumberLast4, ifsc: f.ifsc, gatewayProvider: f.gatewayProvider, ...(account ? {} : { currency: f.currency || undefined }) };
      if (account) await api.patch(`/crm/banking/accounts/${account._id}`, body);
      else await api.post('/crm/banking/accounts', body);
      resetBankAccountCache();
      onSaved();
    } catch (e) { alertService.error('Account not saved', errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title={account ? `Edit ${account.name}` : 'Add account'} onClose={onClose}
      footer={<><Btn onClick={onClose}>Cancel</Btn><Btn primary disabled={saving || !f.name} onClick={save}>{saving ? 'Saving…' : 'Save'}</Btn></>}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={LABEL} htmlFor="ba-kind">Type</label>
          <select id="ba-kind" className={INPUT} value={f.kind} disabled={!!account} onChange={(e) => setF({ ...f, kind: e.target.value })}>
            <option value="bank">Bank account</option><option value="cash">Cash / petty cash</option><option value="gateway">Payment gateway</option>
          </select>
        </div>
        <div><label className={LABEL} htmlFor="ba-name">Name</label><input id="ba-name" className={INPUT} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. HDFC Current" /></div>
        {f.kind === 'bank' && (
          <>
            <div><label className={LABEL} htmlFor="ba-bank">Bank</label><input id="ba-bank" className={INPUT} value={f.bankName} onChange={(e) => setF({ ...f, bankName: e.target.value })} /></div>
            <div><label className={LABEL} htmlFor="ba-l4">Account number (last 4)</label><input id="ba-l4" className={INPUT} maxLength={4} value={f.accountNumberLast4} onChange={(e) => setF({ ...f, accountNumberLast4: e.target.value.replace(/\D/g, '') })} /></div>
            <div><label className={LABEL} htmlFor="ba-ifsc">IFSC</label><input id="ba-ifsc" className={INPUT} value={f.ifsc} onChange={(e) => setF({ ...f, ifsc: e.target.value.toUpperCase() })} /></div>
          </>
        )}
        {f.kind === 'gateway' && (
          <div>
            <label className={LABEL} htmlFor="ba-gw">Gateway</label>
            <select id="ba-gw" className={INPUT} value={f.gatewayProvider} disabled={!!account} onChange={(e) => setF({ ...f, gatewayProvider: e.target.value })}>
              <option value="">Choose…</option>{['razorpay', 'stripe', 'paypal', 'cashfree', 'phonepe', 'payu', 'ccavenue'].map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
        )}
        {!account && <div><label className={LABEL} htmlFor="ba-cur">Currency</label><input id="ba-cur" className={INPUT} maxLength={3} placeholder="Company currency" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value.toUpperCase() })} /></div>}
      </div>
      {f.kind === 'gateway' && <p className="text-[12px] text-[#706E6B]">Online payments from this gateway are held here until you record the settlement to your bank, with the gateway fee.</p>}
    </Modal>
  );
}

function RegisterModal({ account, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/crm/banking/accounts/${account._id}/register`).then((r) => setData(r.data)).catch((e) => alertService.error('Could not load the register', errMsg(e)));
  }, [account._id]);
  return (
    <Modal wide title={`${account.name} register`} onClose={onClose}>
      {!data ? <p className="text-[13px] text-[#706E6B]">Loading…</p> : (
        <Table empty={data.rows.length ? null : 'No money has moved through this account yet.'}
          head={<><Th>Date</Th><Th>Details</Th><Th right>In</Th><Th right>Out</Th><Th right>Balance</Th><Th>Bank</Th></>}>
          {[...data.rows].reverse().map((r) => (
            <tr key={r.entryId} className="border-t border-[#F3F2F2]">
              <Td>{fmtDate(r.date)}</Td>
              <Td><span className="block">{r.memo}</span><span className="text-[11px] text-[#706E6B]">{SOURCE_LABEL[r.sourceType] || r.sourceType}</span></Td>
              <Td right>{r.amount > 0 ? formatCurrency(r.amount, data.currency) : ''}</Td>
              <Td right>{r.amount < 0 ? formatCurrency(-r.amount, data.currency) : ''}</Td>
              <Td right>{formatCurrency(r.balance, data.currency)}</Td>
              <Td>{r.reconciled ? <Pill tone="good">Cleared</Pill> : <Pill>Not on statement</Pill>}</Td>
            </tr>
          ))}
        </Table>
      )}
    </Modal>
  );
}

function AccountsTab({ accounts, reload, onReconcile }) {
  const [edit, setEdit] = useState(null);
  const [register, setRegister] = useState(null);
  const act = async (a, body, done) => {
    try { await api.patch(`/crm/banking/accounts/${a._id}`, body); resetBankAccountCache(); alertService.success(done); reload(); } catch (e) { alertService.error('Not changed', errMsg(e)); }
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end"><Btn primary onClick={() => setEdit({})}><Plus size={14} className="inline -mt-0.5 mr-1" />Add account</Btn></div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {accounts.map((a) => {
          const diff = a.statementBalance === null ? null : Math.round((a.statementBalance - a.balance) * 100) / 100;
          return (
            <div key={a._id} className={cn('bg-white border border-[#E5E5E5] rounded-lg p-4 flex flex-col gap-2 shadow-[0_1px_3px_rgba(0,0,0,0.08)]', !a.isActive && 'opacity-60')}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-[14px] truncate">{a.name}</div>
                  <div className="text-[11px] text-[#706E6B]">
                    {KIND_LABEL[a.kind]}{a.gatewayProvider ? ` · ${a.gatewayProvider}` : ''}{a.bankName ? ` · ${a.bankName}` : ''}{a.accountNumberLast4 ? ` ••${a.accountNumberLast4}` : ''} · {a.currency}
                  </div>
                </div>
                <div className="flex gap-1 flex-wrap justify-end">
                  {a.isDefault && <Pill tone="good">Default</Pill>}
                  {!a.isActive && <Pill>Inactive</Pill>}
                </div>
              </div>
              <div className="text-[22px] font-semibold tabular-nums">{formatCurrency(a.balance, a.currency)}</div>
              <div className="text-[12px] text-[#706E6B]">
                {a.statementBalance === null ? 'No statement imported' : (
                  <>Bank says {formatCurrency(a.statementBalance, a.currency)} on {fmtDate(a.statementDate)}
                    {diff ? <span className="text-[#BA0517]"> · {formatCurrency(diff, a.currency)} difference</span> : <span className="text-[#2E844A]"> · in agreement</span>}</>
                )}
                {a.unmatchedLines > 0 && <span className="block text-[#7A5300]">{a.unmatchedLines} statement line(s) to reconcile</span>}
              </div>
              <div className="flex flex-wrap gap-2 pt-1 text-[12px]">
                <button type="button" className="text-[#0176D3] hover:underline" onClick={() => setRegister(a)}>Register</button>
                {a.kind !== 'cash' && <button type="button" className="text-[#0176D3] hover:underline" onClick={() => onReconcile(a._id)}>Reconcile</button>}
                <button type="button" className="text-[#0176D3] hover:underline" onClick={() => setEdit(a)}>Edit</button>
                {a.isActive && !a.isDefault && a.kind !== 'gateway' && <button type="button" className="text-[#0176D3] hover:underline" onClick={() => act(a, { isDefault: true }, `${a.name} is now the default account`)}>Make default</button>}
                {!a.isDefault && <button type="button" className="text-[#706E6B] hover:underline" onClick={() => act(a, { isActive: !a.isActive }, a.isActive ? 'Account deactivated' : 'Account reactivated')}>{a.isActive ? 'Deactivate' : 'Reactivate'}</button>}
              </div>
            </div>
          );
        })}
      </div>
      {edit && <AccountModal account={edit._id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
      {register && <RegisterModal account={register} onClose={() => setRegister(null)} />}
    </div>
  );
}

// ── Reconcile ────────────────────────────────────────────────────────────

function ReconcileTab({ accounts, accountId, setAccountId, reloadAccounts }) {
  const [status, setStatus] = useState('unmatched');
  const [lines, setLines] = useState([]);
  const [summary, setSummary] = useState(null);
  const [counter, setCounter] = useState([]);
  const [open, setOpen] = useState(null); // { lineId, mode: 'match'|'create', suggestions }
  const [dateFormat, setDateFormat] = useState('dmy');
  const [busy, setBusy] = useState(false);
  const account = accounts.find((a) => a._id === accountId);

  const load = useCallback(async () => {
    if (!accountId) return;
    try {
      const [l, s] = await Promise.all([
        api.get(`/crm/banking/accounts/${accountId}/statement-lines`, { params: { status } }),
        api.get(`/crm/banking/accounts/${accountId}/reconciliation`),
      ]);
      setLines(l.data.lines || []);
      setSummary(s.data);
    } catch (e) { alertService.error('Could not load the statement', errMsg(e)); }
  }, [accountId, status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/crm/banking/counter-accounts').then((r) => setCounter(r.data.accounts || [])).catch(() => {}); }, []);

  const refresh = () => { setOpen(null); load(); reloadAccounts(); };
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return alertService.error('File too large', 'Statements up to 5 MB can be imported');
    setBusy(true);
    try {
      const csv = await file.text();
      const r = await api.post(`/crm/banking/accounts/${accountId}/statements`, { csv, dateFormat });
      alertService.success('Statement imported', `${r.data.imported} new line(s), ${r.data.duplicates} already imported, ${r.data.autoMatched} matched automatically`);
      refresh();
    } catch (e) { alertService.error('Import failed', errMsg(e)); } finally { setBusy(false); }
  };
  const call = async (url, body, done) => {
    try { await api.post(url, body); if (done) alertService.success(done); refresh(); } catch (e) { alertService.error('Not done', errMsg(e)); }
  };
  const findMatches = async (line) => {
    try {
      const r = await api.get(`/crm/banking/statement-lines/${line._id}/suggestions`);
      setOpen({ lineId: line._id, mode: 'match', suggestions: r.data.suggestions || [] });
    } catch (e) { alertService.error('Could not find matches', errMsg(e)); }
  };
  const cur = account?.currency;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-64">
          <label className={LABEL} htmlFor="rc-acct">Account</label>
          <select id="rc-acct" className={INPUT} value={accountId || ''} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">Choose…</option>
            {accounts.filter((a) => a.kind !== 'cash').map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
          </select>
        </div>
        {accountId && (
          <>
            <div className="w-40">
              <label className={LABEL} htmlFor="rc-df">Dates in file</label>
              <select id="rc-df" className={INPUT} value={dateFormat} onChange={(e) => setDateFormat(e.target.value)}>
                <option value="dmy">Day first (31/12)</option><option value="mdy">Month first (12/31)</option>
              </select>
            </div>
            <label className={cn('h-9 px-3 rounded bg-[#0176D3] text-white text-[13px] font-semibold flex items-center gap-1 cursor-pointer', busy && 'opacity-60 pointer-events-none')}>
              <FileUp size={14} />{busy ? 'Importing…' : 'Import CSV statement'}
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
            </label>
            <Btn onClick={() => call(`/crm/banking/accounts/${accountId}/auto-match`, {}, 'Auto-match finished')}><Wand2 size={14} className="inline -mt-0.5 mr-1" />Auto-match</Btn>
          </>
        )}
      </div>

      {!accountId && <p className="text-[13px] text-[#706E6B]">Choose an account, then import its statement exported from net banking as CSV. Lines are matched to receipts, payments and transfers already in the books.</p>}

      {summary && accountId && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ['Bank statement balance', summary.statementBalance === null ? '—' : formatCurrency(summary.statementBalance, cur), summary.statementDate ? `as of ${fmtDate(summary.statementDate)}` : 'No statement imported'],
            ['Balance in books', formatCurrency(summary.ledgerBalance, cur), 'same date'],
            ['Difference', summary.difference === null ? '—' : formatCurrency(summary.difference, cur), summary.difference === null ? 'The statement has no balance column' : summary.difference ? 'Explained by the items below' : 'Books agree with the bank'],
            ['Still to reconcile', `${summary.unmatchedLines.count} bank · ${summary.notOnStatement.count} books`, `${formatCurrency(summary.unmatchedLines.total, cur)} / ${formatCurrency(summary.notOnStatement.total, cur)}`],
          ].map(([l, v, h]) => (
            <div key={l} className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-3">
              <div className="text-[11px] uppercase tracking-wide text-[#706E6B]">{l}</div>
              <div className={cn('text-[17px] font-semibold tabular-nums', l === 'Difference' && summary.difference ? 'text-[#BA0517]' : '')}>{v}</div>
              {h && <div className="text-[11px] text-[#706E6B]">{h}</div>}
            </div>
          ))}
        </div>
      )}

      {accountId && (
        <>
          <div className="flex gap-1">
            {['unmatched', 'matched', 'ignored'].map((s) => (
              <button key={s} type="button" onClick={() => setStatus(s)} className={cn('h-8 px-3 rounded text-[12px] font-semibold capitalize border whitespace-nowrap shrink-0', status === s ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white border-[#D8DDE6]')}>{s}</button>
            ))}
          </div>
          <Table empty={lines.length ? null : status === 'unmatched' ? 'Nothing left to reconcile.' : `No ${status} lines.`}
            head={<><Th>Date</Th><Th>Bank description</Th><Th right>Amount</Th><Th>In the books</Th><Th right>{' '}</Th></>}>
            {lines.map((l) => (
              <React.Fragment key={l._id}>
                <tr className="border-t border-[#F3F2F2] align-top">
                  <Td>{fmtDate(l.date)}</Td>
                  <Td><span className="block">{l.description || '—'}</span>{l.reference && <span className="text-[11px] text-[#706E6B]">Ref {l.reference}</span>}</Td>
                  <Td right className={l.amount > 0 ? 'text-[#2E844A]' : ''}>{formatCurrency(l.amount, cur)}</Td>
                  <Td>{l.status === 'matched' && l.matchedEntryId ? <span>{l.matchedEntryId.memo}<span className="block text-[11px] text-[#706E6B]">{fmtDate(l.matchedEntryId.date)}</span></span> : l.status === 'ignored' ? <Pill>Ignored</Pill> : <Pill tone="warn">Not matched</Pill>}</Td>
                  <Td right>
                    <div className="flex gap-2 justify-end text-[12px] whitespace-nowrap">
                      {l.status === 'unmatched' && (
                        <>
                          <button type="button" className="text-[#0176D3] hover:underline" onClick={() => findMatches(l)}>Match</button>
                          <button type="button" className="text-[#0176D3] hover:underline" onClick={() => setOpen({ lineId: l._id, mode: 'create', counterAccountId: '', memo: l.description })}>Record</button>
                          <button type="button" className="text-[#706E6B] hover:underline" onClick={() => call(`/crm/banking/statement-lines/${l._id}/ignore`)}>Ignore</button>
                        </>
                      )}
                      {l.status !== 'unmatched' && <button type="button" className="text-[#706E6B] hover:underline" onClick={() => call(`/crm/banking/statement-lines/${l._id}/unmatch`)}>Undo</button>}
                    </div>
                  </Td>
                </tr>
                {open?.lineId === l._id && (
                  <tr className="bg-[#F8FAFC]"><td colSpan={5} className="px-3 py-3">
                    {open.mode === 'match' ? (
                      open.suggestions.length === 0 ? (
                        <p className="text-[12px] text-[#706E6B]">Nothing in the books moves {formatCurrency(l.amount, cur)} through this account within a week of this date. Record the receipt or payment first, or use “Record” for charges and interest.</p>
                      ) : (
                        <div className="flex flex-col gap-1">
                          {open.suggestions.map((s) => (
                            <div key={s.entryId} className="flex items-center justify-between gap-3 text-[12px]">
                              <span>{fmtDate(s.date)} · {s.memo} {s.referenceMatch && <Pill tone="good">Reference matches</Pill>} <span className="text-[#706E6B]">{s.daysApart ? `${s.daysApart} day(s) apart` : 'same day'}</span></span>
                              <Btn primary className="h-7 px-3" onClick={() => call(`/crm/banking/statement-lines/${l._id}/match`, { entryId: s.entryId }, 'Matched')}>Match</Btn>
                            </div>
                          ))}
                        </div>
                      )
                    ) : (
                      <div className="flex flex-wrap items-end gap-2">
                        <div className="w-72">
                          <label className={LABEL} htmlFor={`ce-${l._id}`}>{l.amount < 0 ? 'Paid for' : 'Received for'}</label>
                          <select id={`ce-${l._id}`} className={INPUT} value={open.counterAccountId} onChange={(e) => setOpen({ ...open, counterAccountId: e.target.value })}>
                            <option value="">Choose account…</option>
                            {counter.map((c) => <option key={c._id} value={c._id}>{c.code} · {c.name}</option>)}
                          </select>
                        </div>
                        <div className="flex-1 min-w-[200px]"><label className={LABEL} htmlFor={`cm-${l._id}`}>Note</label><input id={`cm-${l._id}`} className={INPUT} value={open.memo || ''} onChange={(e) => setOpen({ ...open, memo: e.target.value })} /></div>
                        <Btn primary disabled={!open.counterAccountId} onClick={() => call(`/crm/banking/statement-lines/${l._id}/create-entry`, { counterAccountId: open.counterAccountId, memo: open.memo }, 'Recorded and matched')}>Record and match</Btn>
                        <p className="w-full text-[11px] text-[#706E6B]">For bank charges, interest and similar. Customer and vendor money should be recorded as a receipt or vendor payment, then matched.</p>
                      </div>
                    )}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </Table>
          {summary?.notOnStatement.count > 0 && status === 'unmatched' && (
            <details className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-3 text-[12px]">
              <summary className="cursor-pointer font-semibold">In the books but not on the statement ({summary.notOnStatement.count})</summary>
              <p className="text-[#706E6B] my-2">Usually cheques not yet presented or deposits in transit. Old items here may be missing from the bank or recorded twice.</p>
              {summary.notOnStatement.rows.map((r) => (
                <div key={r.entryId} className="flex justify-between border-t border-[#EEF1F6] py-1.5"><span>{fmtDate(r.date)} · {r.memo}</span><span className="tabular-nums">{formatCurrency(r.amount, cur)}</span></div>
              ))}
            </details>
          )}
        </>
      )}
    </div>
  );
}

// ── Transfers & settlements ──────────────────────────────────────────────

function TransactionModal({ accounts, onClose, onSaved }) {
  const [f, setF] = useState({ kind: 'transfer', date: localToday(), amount: '', fee: '', feeTax: '', fromAccountId: '', toAccountId: '', bankAccountId: '', direction: 'out', counterAccountId: '', reference: '', memo: '' });
  const [counter, setCounter] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/crm/banking/counter-accounts').then((r) => setCounter(r.data.accounts || [])).catch(() => {}); }, []);
  const active = accounts.filter((a) => a.isActive);
  const gateways = active.filter((a) => a.kind === 'gateway');
  const money = active.filter((a) => a.kind !== 'gateway');
  const net = Math.round(((Number(f.amount) || 0) - (Number(f.fee) || 0) - (Number(f.feeTax) || 0)) * 100) / 100;
  const opt = (list) => [<option key="" value="">Choose…</option>, ...list.map((a) => <option key={a._id} value={a._id}>{a.name} ({a.currency})</option>)];
  const save = async () => {
    setSaving(true);
    try { await api.post('/crm/banking/transactions', f); onSaved(); } catch (e) { alertService.error('Not recorded', errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Record bank transaction" onClose={onClose}
      footer={<><Btn onClick={onClose}>Cancel</Btn><Btn primary disabled={saving || !(Number(f.amount) > 0)} onClick={save}>{saving ? 'Saving…' : 'Record'}</Btn></>}>
      <div className="flex gap-1">
        {[['transfer', 'Transfer'], ['settlement', 'Gateway settlement'], ['entry', 'Charge / interest / other']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setF({ ...f, kind: k })} className={cn('h-8 px-3 rounded text-[12px] font-semibold border whitespace-nowrap shrink-0', f.kind === k ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white border-[#D8DDE6]')}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {f.kind === 'transfer' && (
          <>
            <div><label className={LABEL} htmlFor="tx-from">From</label><select id="tx-from" className={INPUT} value={f.fromAccountId} onChange={(e) => setF({ ...f, fromAccountId: e.target.value })}>{opt(money)}</select></div>
            <div><label className={LABEL} htmlFor="tx-to">To</label><select id="tx-to" className={INPUT} value={f.toAccountId} onChange={(e) => setF({ ...f, toAccountId: e.target.value })}>{opt(money)}</select></div>
          </>
        )}
        {f.kind === 'settlement' && (
          <>
            <div><label className={LABEL} htmlFor="tx-gw">Gateway</label><select id="tx-gw" className={INPUT} value={f.fromAccountId} onChange={(e) => setF({ ...f, fromAccountId: e.target.value })}>{opt(gateways)}</select></div>
            <div><label className={LABEL} htmlFor="tx-bank">Paid into</label><select id="tx-bank" className={INPUT} value={f.toAccountId} onChange={(e) => setF({ ...f, toAccountId: e.target.value })}>{opt(money)}</select></div>
          </>
        )}
        {f.kind === 'entry' && (
          <>
            <div><label className={LABEL} htmlFor="tx-acct">Account</label><select id="tx-acct" className={INPUT} value={f.bankAccountId} onChange={(e) => setF({ ...f, bankAccountId: e.target.value })}>{opt(money)}</select></div>
            <div><label className={LABEL} htmlFor="tx-dir">Direction</label><select id="tx-dir" className={INPUT} value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value })}><option value="out">Money out</option><option value="in">Money in</option></select></div>
            <div className="col-span-2"><label className={LABEL} htmlFor="tx-ctr">{f.direction === 'out' ? 'Paid for' : 'Received for'}</label>
              <select id="tx-ctr" className={INPUT} value={f.counterAccountId} onChange={(e) => setF({ ...f, counterAccountId: e.target.value })}>
                <option value="">Choose account…</option>{counter.map((c) => <option key={c._id} value={c._id}>{c.code} · {c.name}</option>)}
              </select></div>
          </>
        )}
        <div><label className={LABEL} htmlFor="tx-date">Date</label><input id="tx-date" type="date" className={INPUT} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
        <div><label className={LABEL} htmlFor="tx-amt">{f.kind === 'settlement' ? 'Gross collected' : 'Amount'}</label><input id="tx-amt" type="number" min="0" step="0.01" className={INPUT} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></div>
        {f.kind === 'settlement' && (
          <>
            <div><label className={LABEL} htmlFor="tx-fee">Gateway fee</label><input id="tx-fee" type="number" min="0" step="0.01" className={INPUT} value={f.fee} onChange={(e) => setF({ ...f, fee: e.target.value })} /></div>
            <div><label className={LABEL} htmlFor="tx-ft">GST on fee</label><input id="tx-ft" type="number" min="0" step="0.01" className={INPUT} value={f.feeTax} onChange={(e) => setF({ ...f, feeTax: e.target.value })} /></div>
            <p className="col-span-2 text-[12px] text-[#706E6B]">Bank receives <strong className="tabular-nums">{net}</strong>. The fee is booked to Bank and Gateway Charges; GST on it is claimed as input credit.</p>
          </>
        )}
        <div><label className={LABEL} htmlFor="tx-ref">Reference</label><input id="tx-ref" className={INPUT} value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} /></div>
        <div><label className={LABEL} htmlFor="tx-memo">Note</label><input id="tx-memo" className={INPUT} value={f.memo} onChange={(e) => setF({ ...f, memo: e.target.value })} /></div>
      </div>
    </Modal>
  );
}

function TransactionsTab({ accounts, reloadAccounts }) {
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => api.get('/crm/banking/transactions').then((r) => setRows(r.data.transactions || [])).catch((e) => alertService.error('Could not load', errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const voidTx = async (t) => {
    if (!(await alertService.confirm('Void this transaction?', 'A reversing entry is posted; the original stays in the books.', 'warning', 'Void'))) return;
    try { await api.post(`/crm/banking/transactions/${t._id}/void`); alertService.success('Voided'); load(); reloadAccounts(); } catch (e) { alertService.error('Not voided', errMsg(e)); }
  };
  const describe = (t) => (t.kind === 'transfer' ? `${t.fromAccountId?.name} → ${t.toAccountId?.name}`
    : t.kind === 'settlement' ? `${t.fromAccountId?.name} → ${t.toAccountId?.name}`
      : `${t.direction === 'in' ? 'Into' : 'From'} ${t.bankAccountId?.name} · ${t.counterAccountId?.name || ''}`);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end"><Btn primary onClick={() => setAdding(true)}><Plus size={14} className="inline -mt-0.5 mr-1" />Record transaction</Btn></div>
      <Table empty={rows.length ? null : 'No transfers, settlements or bank entries yet.'}
        head={<><Th>Date</Th><Th>Type</Th><Th>Details</Th><Th right>Amount</Th><Th right>Fees</Th><Th>{' '}</Th></>}>
        {rows.map((t) => (
          <tr key={t._id} className={cn('border-t border-[#EEF1F6]', t.status === 'void' && 'opacity-50')}>
            <Td>{fmtDate(t.date)}</Td>
            <Td className="capitalize">{t.kind === 'entry' ? 'Bank entry' : t.kind}</Td>
            <Td>{describe(t)}{(t.reference || t.memo) && <span className="block text-[11px] text-[#706E6B]">{[t.reference, t.memo].filter(Boolean).join(' · ')}</span>}</Td>
            <Td right>{formatCurrency(t.amount, t.currency)}</Td>
            <Td right>{t.fee || t.feeTax ? formatCurrency((t.fee || 0) + (t.feeTax || 0), t.currency) : ''}</Td>
            <Td>{t.status === 'void' ? <Pill>Void</Pill> : <button type="button" className="text-[12px] text-[#BA0517] hover:underline" onClick={() => voidTx(t)}>Void</button>}</Td>
          </tr>
        ))}
      </Table>
      {adding && <TransactionModal accounts={accounts} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); reloadAccounts(); }} />}
    </div>
  );
}

// ── Cheques ──────────────────────────────────────────────────────────────

function ChequesTab({ reloadAccounts }) {
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get('/crm/banking/cheques', { params: { status } }).then((r) => setRows(r.data.cheques || [])).catch((e) => alertService.error('Could not load', errMsg(e))), [status]);
  useEffect(() => { load(); }, [load]);
  const act = async (c, action) => {
    if (action === 'bounce' && !(await alertService.confirm(`Mark cheque ${c.chequeNumber || ''} as bounced?`, `The ${c.kind === 'received' ? 'receipt' : 'payment'} is voided and the ${c.kind === 'received' ? 'invoices' : 'bills'} it paid reopen.`, 'warning', 'Bounced'))) return;
    try { await api.post(`/crm/banking/cheques/${c.kind}/${c.id}/${action}`); alertService.success(action === 'clear' ? 'Cheque cleared' : 'Cheque bounced'); load(); reloadAccounts(); } catch (e) { alertService.error('Not changed', errMsg(e)); }
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1">
        {['pending', 'cleared', 'bounced', 'all'].map((s) => (
          <button key={s} type="button" onClick={() => setStatus(s)} className={cn('h-8 px-3 rounded text-[12px] font-semibold capitalize border whitespace-nowrap shrink-0', status === s ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white border-[#D8DDE6]')}>{s}</button>
        ))}
      </div>
      <Table empty={rows.length ? null : 'No cheques here. Cheques appear when a receipt or vendor payment is recorded with the cheque method.'}
        head={<><Th>Date</Th><Th>Cheque</Th><Th>From / to</Th><Th>Account</Th><Th right>Amount</Th><Th>Status</Th><Th>{' '}</Th></>}>
        {rows.map((c) => (
          <tr key={`${c.kind}-${c.id}`} className="border-t border-[#F3F2F2]">
            <Td>{fmtDate(c.date)}</Td>
            <Td>{c.chequeNumber || '—'}<span className="block text-[11px] text-[#706E6B]">{c.kind === 'received' ? 'Received' : 'Issued'}{c.chequeDate ? ` · dated ${fmtDate(c.chequeDate)}` : ''}</span></Td>
            <Td>{c.party}</Td>
            <Td>{c.bankAccount}</Td>
            <Td right>{formatCurrency(c.amount, c.currency)}</Td>
            <Td><Pill tone={c.status === 'cleared' ? 'good' : c.status === 'bounced' ? 'bad' : 'warn'}>{c.status}</Pill>{c.clearedAt && <span className="block text-[11px] text-[#706E6B]">{fmtDate(c.clearedAt)}</span>}</Td>
            <Td>{c.status === 'pending' && c.docStatus !== 'void' && (
              <div className="flex gap-2 text-[12px]">
                <button type="button" className="text-[#0176D3] hover:underline" onClick={() => act(c, 'clear')}>Cleared</button>
                <button type="button" className="text-[#BA0517] hover:underline" onClick={() => act(c, 'bounce')}>Bounced</button>
              </div>
            )}</Td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

export default function BankingPage() {
  const [tab, setTab] = useState('accounts');
  const [accounts, setAccounts] = useState([]);
  const [reconcileId, setReconcileId] = useState('');
  const [loading, setLoading] = useState(false);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/crm/banking/accounts'); setAccounts(r.data.accounts || []); } catch (e) { alertService.error('Could not load accounts', errMsg(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const totals = useMemo(() => {
    const by = {};
    accounts.filter((a) => a.isActive).forEach((a) => { by[a.currency] = (by[a.currency] || 0) + a.balance; });
    return Object.entries(by);
  }, [accounts]);

  return (
    <div className={PAGE}>
      <PageHeader title="Banking" subtitle={<>Bank, cash and gateway balances{totals.length ? ': ' : ''}{totals.map(([c, v]) => formatCurrency(v, c)).join(' · ')}</>} icon={Landmark} actions={<>
        <Btn onClick={loadAccounts} aria-label="Refresh"><RefreshCw size={14} className={cn(loading && 'animate-spin')} /></Btn>
      </>} />
      <Tabs tabs={TABS} value={tab} onChange={setTab} />
      {tab === 'accounts' && <AccountsTab accounts={accounts} reload={loadAccounts} onReconcile={(id) => { setReconcileId(id); setTab('reconcile'); }} />}
      {tab === 'reconcile' && <ReconcileTab accounts={accounts} accountId={reconcileId} setAccountId={setReconcileId} reloadAccounts={loadAccounts} />}
      {tab === 'transactions' && <TransactionsTab accounts={accounts} reloadAccounts={loadAccounts} />}
      {tab === 'cheques' && <ChequesTab reloadAccounts={loadAccounts} />}
    </div>
  );
}
